import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { CategoryEntity, NoteEntity } from '@actual-app/core/types/models';
import { useQuery as useQueryRQ } from '@tanstack/react-query';

import { prewarmMonth } from '#components/budget/util';
import { plantillasDe } from '#components/mobile/anual/useAnual';
import { useCategories } from '#hooks/useCategories';
import { useQuery } from '#hooks/useQuery';
import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { aqlQuery } from '#queries/aqlQuery';

import {
  conCuentasNuevas,
  cuentasDePagosFijos,
  TODAS_LAS_CUENTAS,
  unificarCuentasElegidas,
} from './cuentaCategoria';
import { objetivoDesdePlantillas } from './objetivos';
import {
  calcularPagosFijos,
  conCuenta,
  conFrecuencia,
  conIgnorado,
  cuentasPorGuardar,
  desdeHistorial,
  parseFrecuenciasPagosFijos,
  parseIgnorados,
  repartirPestanas,
  resumirMes,
} from './pagosFijos';
import type {
  CuentasPagosFijos,
  EntradaPagoFijo,
  MesesCobro,
  MovimientoHistorial,
  PagoFijo,
  ResumenPagosFijos,
} from './pagosFijos';
import { useDatosObjetivos } from './useDatosObjetivos';
import { usePagadas } from './usePagadas';

/**
 * Cuenta de cada categoría, UN solo dato para la ficha («Cuenta») y para el
 * pago fijo («Desde: …»): pref sincronizada `categorias-cuentas`
 * ({ idCategoría: idCuenta | 'todas' }); mientras no exista, se lee
 * `pagos-fijos-cuentas` (lo de antes) y la primera escritura lo copia.
 */
export function useCuentasElegidas() {
  const [rawElegidas, setRaw] = useSyncedPref('categorias-cuentas');
  const [rawPagosFijos] = useSyncedPref('pagos-fijos-cuentas');
  const elegidas = useMemo(
    () => unificarCuentasElegidas(rawElegidas, rawPagosFijos),
    [rawElegidas, rawPagosFijos],
  );
  /** `null` = «Automática»; `'todas'` = «Todas las cuentas». */
  const setEleccion = useCallback(
    (categoriaId: string, valor: string | null) =>
      setRaw(JSON.stringify(conCuenta(elegidas, categoriaId, valor))),
    [elegidas, setRaw],
  );
  /** Guarda propuestas solo donde no hay nada elegido (ni «todas»). */
  const setNuevas = useCallback(
    (nuevas: CuentasPagosFijos) =>
      setRaw(JSON.stringify(conCuentasNuevas(elegidas, nuevas))),
    [elegidas, setRaw],
  );
  return { elegidas, setEleccion, setNuevas };
}

/**
 * Cuenta de cargo de los pagos fijos: la misma `useCuentasElegidas` vista
 * sin «todas» (un pago fijo con «Todas las cuentas» no tiene cuenta
 * guardada y no se le pregunta por cambios de cuenta).
 */
export function useCuentasPagosFijos() {
  const { elegidas, setEleccion, setNuevas } = useCuentasElegidas();
  const cuentas = useMemo(() => cuentasDePagosFijos(elegidas), [elegidas]);
  return {
    cuentas,
    elegidas,
    setCuenta: setEleccion,
    setCuentas: setNuevas,
  };
}

/**
 * Pref sincronizada `pagos-fijos-frecuencias`: cada cuántos meses se cobra
 * de verdad, corregido a mano (`null` vuelve a la del historial).
 */
export function useFrecuenciasPagosFijos() {
  const [raw, setRaw] = useSyncedPref('pagos-fijos-frecuencias');
  const frecuencias = useMemo(() => parseFrecuenciasPagosFijos(raw), [raw]);
  const setFrecuencia = useCallback(
    (categoriaId: string, meses: MesesCobro | null) =>
      setRaw(JSON.stringify(conFrecuencia(frecuencias, categoriaId, meses))),
    [frecuencias, setRaw],
  );
  return { frecuencias, setFrecuencia };
}

/**
 * Pref sincronizada `pagos-fijos-ignorados`: movimientos de otra cuenta a
 * los que se respondió «No» (no se vuelve a preguntar por ellos).
 */
export function useIgnoradosPagosFijos() {
  const [raw, setRaw] = useSyncedPref('pagos-fijos-ignorados');
  const lista = useMemo(() => parseIgnorados(raw), [raw]);
  const ignorados = useMemo(() => new Set(lista), [lista]);
  const ignorar = useCallback(
    (movimientoId: string) =>
      setRaw(JSON.stringify(conIgnorado(lista, movimientoId))),
    [lista, setRaw],
  );
  return { ignorados, ignorar };
}

/**
 * Guarda sola la cuenta propuesta por el historial en los pagos fijos que
 * no la tienen: una sola vez por categoría y por montaje (sin bucles).
 */
export function useGuardarCuentasPropuestas(
  pagos: readonly Pick<PagoFijo, 'id' | 'cuentaId' | 'cuentaGuardada'>[],
  listo: boolean,
  setCuentas: (nuevas: CuentasPagosFijos) => void,
) {
  const intentadas = useRef(new Set<string>());
  useEffect(() => {
    if (!listo) {
      return;
    }
    const nuevas: CuentasPagosFijos = {};
    for (const [id, cuenta] of Object.entries(cuentasPorGuardar(pagos))) {
      if (!intentadas.current.has(id)) {
        intentadas.current.add(id);
        nuevas[id] = cuenta;
      }
    }
    if (Object.keys(nuevas).length > 0) {
      setCuentas(nuevas);
    }
  }, [pagos, listo, setCuentas]);
}

/**
 * Salidas (sin traspasos) de unas categorías en los últimos 36 meses, por
 * categoría. Base para inferir el día de cargo y la cuenta.
 */
export function useHistorialCategorias(ids: readonly string[], mes: string) {
  const desde = desdeHistorial(mes);
  const clave = [...ids].sort().join(',');
  const { data } = useQueryRQ({
    queryKey: ['pagos-fijos', 'historial', clave, desde, mes],
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: filas } = await aqlQuery(
        q('transactions')
          .filter({
            category: { $oneof: [...ids] },
            amount: { $lt: 0 },
            // Como en Informes: los traspasos a cuentas fuera del
            // presupuesto (préstamo del coche…) con categoría son gasto.
            $or: [
              { 'payee.transfer_acct': null },
              { 'payee.transfer_acct.offbudget': true },
            ],
            $and: [
              { date: { $transform: '$month', $gte: desde } },
              { date: { $transform: '$month', $lte: mes } },
            ],
          })
          .select(['id', 'category', 'date', 'amount', 'account']),
      );
      const porCategoria = new Map<string, MovimientoHistorial[]>();
      for (const f of filas as (MovimientoHistorial & {
        category: string;
      })[]) {
        const lista = porCategoria.get(f.category) ?? [];
        lista.push({
          id: f.id,
          date: f.date,
          amount: f.amount,
          account: f.account,
        });
        porCategoria.set(f.category, lista);
      }
      return porCategoria;
    },
  });
  return data;
}

export type DatosPagosFijos = {
  /** Todos los pagos fijos (mensuales, anuales, una vez y sin objetivo). */
  pagos: PagoFijo[];
  /** Se cobran cada 1-3 meses (cobro real), ordenados por día de cargo. */
  mensuales: PagoFijo[];
  /** Se cobran cada 6-12 meses o una vez, por fecha. */
  anuales: PagoFijo[];
  /** Marcados como pago fijo pero sin objetivo utilizable. */
  sinObjetivo: PagoFijo[];
  resumen: ResumenPagosFijos;
  /** Ids de las categorías marcadas como pago fijo (pref `pagos-fijos`). */
  fijos: Set<string>;
  /** Hasta que los saldos del mes están cargados. */
  cargando: boolean;
  /** Guarda (o quita) la cuenta de cargo de una categoría. */
  setCuenta: (categoriaId: string, cuentaId: string | null) => void;
  /** Marca manual «pagada» / «no pagada» / sin marca, en `month`. */
  marcar: (id: string, pagada: boolean | 'no') => void;
  /** «No» al cambio de cuenta: no volver a preguntar por ese movimiento. */
  ignorarCambio: (movimientoId: string) => void;
};

/**
 * Pagos fijos de `month` con su estado (Pagado / Toca hoy / En camino / Atrasado /
 * Próximo / Programado), día de cargo y cuenta (guardados o inferidos del
 * historial). Lo usan la pantalla «Pagos fijos», «Asignar el mes» y el
 * Inicio («Por hacer», «Cuenta común»).
 */
export function usePagosFijos(month: string): DatosPagosFijos {
  const spreadsheet = useSpreadsheet();
  const { data: { grouped } = { grouped: [] } } = useCategories();
  const { fijos, manuales, marcar } = usePagadas(month);
  const { cuentas, elegidas, setCuenta, setCuentas } = useCuentasPagosFijos();
  const todas = useMemo(
    () =>
      new Set(
        Object.keys(elegidas).filter(id => elegidas[id] === TODAS_LAS_CUENTAS),
      ),
    [elegidas],
  );
  const { ignorados, ignorar } = useIgnoradosPagosFijos();
  const { frecuencias } = useFrecuenciasPagosFijos();
  const { data: notas } = useQuery<NoteEntity>(
    () => q('notes').select('*'),
    [],
  );

  const [listo, setListo] = useState(false);
  useEffect(() => {
    let cancelado = false;
    setListo(false);
    void prewarmMonth(spreadsheet, month).then(() => {
      if (!cancelado) {
        setListo(true);
      }
    });
    return () => {
      cancelado = true;
    };
  }, [spreadsheet, month]);

  const base = useMemo(() => {
    const notaDe = new Map((notas ?? []).map(n => [n.id, n.note]));
    const categorias: CategoryEntity[] = [];
    const entradas: Omit<EntradaPagoFijo, 'historial'>[] = [];
    for (const grupo of grouped) {
      for (const categoria of grupo.categories ?? []) {
        if (!fijos.has(categoria.id)) {
          continue;
        }
        const plantillas = plantillasDe(categoria.goal_def);
        categorias.push(categoria);
        entradas.push({
          id: categoria.id,
          nombre: categoria.name,
          grupoId: grupo.id,
          grupoNombre: grupo.name,
          objetivo:
            plantillas.length > 0
              ? objetivoDesdePlantillas(plantillas, notaDe.get(categoria.id))
              : null,
        });
      }
    }
    return { categorias, entradas };
  }, [grouped, notas, fijos]);

  const ids = useMemo(() => base.categorias.map(c => c.id), [base]);
  const historial = useHistorialCategorias(ids, month);
  const datosMes = useDatosObjetivos(month, base.categorias);

  const resultado = useMemo(() => {
    const pagos = calcularPagosFijos({
      entradas: base.entradas.map(e => ({
        ...e,
        historial: historial?.get(e.id) ?? [],
      })),
      fijos,
      manuales,
      cuentas,
      frecuencias,
      ignorados,
      todas,
      datosMes,
      mes: month,
      hoy: monthUtils.currentDay(),
    });
    const { mensuales, anuales } = repartirPestanas(pagos);
    const sinObjetivo = pagos.filter(p => p.frecuencia == null);
    return {
      pagos,
      mensuales,
      anuales,
      sinObjetivo,
      resumen: resumirMes(pagos, month),
      fijos,
      cargando: !listo,
      setCuenta,
      marcar,
      ignorarCambio: ignorar,
    };
  }, [
    base,
    historial,
    fijos,
    manuales,
    cuentas,
    frecuencias,
    ignorados,
    todas,
    datosMes,
    month,
    listo,
    setCuenta,
    marcar,
    ignorar,
  ]);

  // Sin historial cargado aún no se guarda nada (la propuesta sería nula).
  useGuardarCuentasPropuestas(
    resultado.pagos,
    historial != null || ids.length === 0,
    setCuentas,
  );

  return resultado;
}
